import PropTypes from "prop-types";

/* Verbatim port of the design prototype's Doughnut/DoughnutCard (viz2.jsx):
   pure-SVG donut with a side legend, so typography and colors inherit the
   rec tokens exactly. data: [{label, value, color?}] */
const PALETTE = [
  "var(--accent)",
  "var(--warm)",
  "var(--info)",
  "var(--success)",
  "var(--danger)",
  "var(--accent-deep)",
];

const fmt = (v, d = 0) =>
  Number(v ?? 0).toLocaleString("pt-PT", { minimumFractionDigits: d, maximumFractionDigits: d });

function Doughnut({ data, size, thickness, centerLabel, centerValue }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const r = size / 2 - thickness / 2 - 2;
  const c = size / 2;
  // pathLength normalizes the circle's length to 100 so the dash segments
  // join exactly — without it the browser's real path length drifts from
  // 2πr and a sliver of the repeated dash pattern shows at the seam.
  const L = 100;
  let acc = 0;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ display: "block" }}>
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        style={{ stroke: "var(--track)" }}
        strokeWidth={thickness}
      />
      {data.map((d, i) => {
        const frac = d.value / total;
        const dash = L * frac;
        const el = (
          <circle
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            cx={c}
            cy={c}
            r={r}
            fill="none"
            style={{ stroke: d.color }}
            strokeWidth={thickness}
            pathLength={L}
            strokeDasharray={`${dash} ${L - dash}`}
            strokeDashoffset={-acc * L}
            transform={`rotate(-90 ${c} ${c})`}
            strokeLinecap="butt"
          />
        );
        acc += frac;
        return el;
      })}
      {centerValue != null && (
        <text
          x={c}
          y={c - 2}
          textAnchor="middle"
          fontSize="21"
          fontWeight="700"
          style={{ fill: "var(--ink)", letterSpacing: "-.5px" }}
        >
          {centerValue}
        </text>
      )}
      {centerLabel && (
        <text
          x={c}
          y={c + 15}
          textAnchor="middle"
          fontSize="10.5"
          fontWeight="500"
          style={{ fill: "var(--ink-3)" }}
        >
          {centerLabel}
        </text>
      )}
    </svg>
  );
}

Doughnut.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, color: PropTypes.string }))
    .isRequired,
  size: PropTypes.number,
  thickness: PropTypes.number,
  centerLabel: PropTypes.node,
  centerValue: PropTypes.node,
};

Doughnut.defaultProps = {
  size: 150,
  thickness: 22,
  centerLabel: null,
  centerValue: null,
};

export default function DonutCard({ title, data, unit }) {
  const colored = data.map((d, i) => ({ ...d, color: d.color || PALETTE[i % PALETTE.length] }));
  const total = colored.reduce((s, d) => s + d.value, 0);
  return (
    <div className="rec-card rec-card-pad">
      <div className="text-[15px] font-bold text-ink" style={{ marginBottom: 14 }}>
        {title}
      </div>
      <div className="flex items-center gap-5">
        <Doughnut data={colored} size={148} centerValue={fmt(total, 0)} centerLabel={unit} />
        <div className="flex-1 flex flex-col gap-[9px] min-w-0">
          {colored.map((d) => (
            <div key={d.label} className="flex items-center gap-[9px]">
              <i
                className="shrink-0"
                style={{ width: 10, height: 10, borderRadius: 3, background: d.color }}
              />
              <span className="flex-1 text-[12.5px] whitespace-nowrap overflow-hidden text-ellipsis text-ink-2">
                {d.label}
              </span>
              <b className="text-[12.5px] text-ink tabular-nums whitespace-nowrap">
                {`${fmt(d.value, 0)}${unit ? ` ${unit}` : ""}`}
              </b>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

DonutCard.propTypes = {
  title: PropTypes.node.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number,
      color: PropTypes.string,
    })
  ).isRequired,
  unit: PropTypes.string,
};

DonutCard.defaultProps = {
  unit: "",
};
